import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { hasObservatoryAdminCapabilities } from '../../utils/userCapabilities';

const AdminRoute = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Routes that observatory / organization admins can access (not just
  // server admins). Org owners and admins reach here via the Admin
  // Control Center; the Manage Members / Email / Edit / etc. row
  // actions on the Organizations pane all open /admin/organizations.
  const observatoryAdminAccessibleRoutes = [
    '/admin/dashboard',
    '/admin/control-center',
    '/admin/observatories',
    '/admin/observations',
    '/admin/announcements',
    '/admin/users',
    '/admin/users-full',
    '/admin/analytics-pane',
    '/admin/organizations',
    '/admin/billing/dashboard',
    '/admin/billing/explorer',
    '/admin/billing/users',
    '/admin/billing/projects',
    '/admin/billing/observatories'
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);

        // Check if user has server admin role first
        const isServerAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
        
        if (isServerAdmin) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // For non-server admins, check if this route allows observatory admins
        const currentPath = location.pathname;
        const allowsObservatoryAdmin = observatoryAdminAccessibleRoutes.some(route => 
          currentPath.startsWith(route)
        );

        if (allowsObservatoryAdmin && (user?.id || user?._id)) {
          // Check whether the user has observatory-admin capabilities
          // OR is an org owner / org admin. Org owners reach the
          // Control Center to manage members / send announcements
          // for their orgs even without any observatory role.
          const userId = user.id || user._id;
          const [obsRes, orgRes] = await Promise.all([
            apiService.getUserMemberships(userId).catch(() => null),
            apiService.getUserOrganizations().catch(() => null),
          ]);

          const userMemberships = obsRes?.data || [];
          const isObservatoryAdmin = hasObservatoryAdminCapabilities(userMemberships);

          // getUserOrganizations returns either { organizations: [...] }
          // or a bare array depending on shape; normalise.
          const orgList = orgRes?.data?.organizations || orgRes?.data || [];
          const isOrgOwnerOrAdmin = Array.isArray(orgList) && orgList.some((row) => {
            // Each row may either be a wrapper { organization, is_owner,
            // permissions } or a flat membership document with the
            // same fields.
            const isOwner = row.is_owner === true;
            const canManage = row.permissions?.can_manage_members
              || row.permissions?.can_manage_observatories
              || row.permissions?.can_manage_projects;
            return isOwner || canManage;
          });

          setHasAccess(isObservatoryAdmin || isOrgOwnerOrAdmin);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, location.pathname]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            maxWidth: 400,
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            You don't have permission to access this area. Admin privileges are required.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              // When the Access Denied screen renders inside a floating
              // iframe (parent !== window), history.back() can pop the
              // *parent* page out of the Control Center. Close the
              // iframe instead and leave the parent's OrgsPane alone.
              const isStandalone = new URLSearchParams(window.location.search).has('standalone');
              if (isStandalone && window.parent !== window) {
                try { window.parent.postMessage({ type: 'closeFloatingWindow' }, '*'); } catch (_) { /* */ }
                return;
              }
              window.history.back();
            }}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default AdminRoute;